/**
 * Plan versus now, as a waterfall.
 *
 * The table underneath is exact and stays the thing you read the numbers off;
 * this is the SHAPE — which single step moved the plan most, and whether the
 * trimester is being delivered or re-cut. A column of signed numbers does not
 * answer that at a glance, which is the whole reason it is here.
 *
 * ⚠️ INLINE SVG, no chart library. Nothing in this repo ships one, and a
 * waterfall is a running total and some rectangles — pulling in a charting
 * dependency for eleven bars would be the expensive way to get less control
 * over the labels, which are the part that has to stay legible at card width.
 *
 * COLOUR CARRIES MEANING, not sequence. Green is delivery (filled). Slate is a
 * deliberate decision that took work out of the trimester (on hold, re-dated,
 * lost its date). Blue is the plan shrinking (cancelled). Rose is the plan
 * GROWING — added roles and roles pulled forward — because that is the half
 * that turns a delivery story into a re-planning one. The two totals are the
 * cockpit's neutral ink.
 */
import type { PlanVsNowCounts } from '../../../types/taTracking';

export interface WaterfallStep {
  key: string;
  label: string;
  /** Signed: negative leaves the trimester, positive joins it. */
  delta: number;
  tone: 'delivered' | 'paused' | 'dropped' | 'added';
}

const TONE_FILL: Record<WaterfallStep['tone'], string> = {
  delivered: '#10b981',
  paused: '#94a3b8',
  dropped: '#60a5fa',
  added: '#fb7185',
};

/**
 * The steps between the two totals, in the order they are read.
 *
 * Zero-delta steps are dropped: a waterfall with four empty columns reads as
 * four things that happened. The table lists every bucket regardless.
 */
export function waterfallSteps(c: PlanVsNowCounts): WaterfallStep[] {
  return (
    [
      { key: 'filled', label: 'Filled', delta: -c.filled, tone: 'delivered' },
      { key: 'onHold', label: 'On hold', delta: -c.onHold, tone: 'paused' },
      { key: 'redated', label: 'Re-dated', delta: -c.redated, tone: 'paused' },
      { key: 'undated', label: 'No date', delta: -c.undated, tone: 'paused' },
      { key: 'dropped', label: 'Cancelled', delta: -c.dropped, tone: 'dropped' },
      {
        key: 'addedBackfill',
        label: 'Backfills',
        delta: c.addedBackfill,
        tone: 'added',
      },
      {
        key: 'addedNew',
        label: 'New roles',
        delta: c.addedNew + c.addedUnknown,
        tone: 'added',
      },
      { key: 'movedIn', label: 'Pulled in', delta: c.movedIn, tone: 'added' },
    ] satisfies WaterfallStep[]
  ).filter((s) => s.delta !== 0);
}

const H = 150;
const LABEL_H = 34;
const GAP = 8;
const MIN_BAR = 2;
const COL = 60;
/**
 * Headroom above the tallest bar, so its value label has somewhere to go.
 *
 * Without it the first bar reached y=0 and its label was drawn at the clamp — on
 * top of a dark fill in dark ink, i.e. invisible. The total was the one figure
 * on the chart nobody could read.
 */
const TOP_PAD = 16;

/**
 * ⚠️ THE AXIS STARTS AT ZERO and the scale covers the highest running total.
 *
 * A waterfall whose baseline floats makes a step of 2 look like a step of 20,
 * which is the one thing a reader takes from a chart without checking. The
 * table is there for the exact figures; this must not disagree with it about
 * which step was the big one.
 */
export function PlanWaterfall({
  counts,
  stillToRecruit,
}: {
  counts: PlanVsNowCounts;
  stillToRecruit: number;
}) {
  const steps = waterfallSteps(counts);

  // Running totals, so each floating bar knows where it starts and ends.
  const bars: {
    key: string;
    label: string;
    from: number;
    to: number;
    delta: number;
    fill: string;
    total: boolean;
  }[] = [];

  bars.push({
    key: 'plan',
    label: 'Planned',
    from: 0,
    to: counts.plan,
    delta: counts.plan,
    fill: '#334155',
    total: true,
  });

  let running = counts.plan;
  for (const s of steps) {
    const next = running + s.delta;
    bars.push({
      key: s.key,
      label: s.label,
      from: Math.min(running, next),
      to: Math.max(running, next),
      delta: s.delta,
      fill: TONE_FILL[s.tone],
      total: false,
    });
    running = next;
  }

  bars.push({
    key: 'left',
    label: 'Still to recruit',
    from: 0,
    to: Math.max(0, stillToRecruit),
    delta: stillToRecruit,
    fill: '#334155',
    total: true,
  });

  const ceiling = Math.max(1, ...bars.map((b) => b.to));
  const scale = (H - TOP_PAD) / ceiling;

  return (
    <div className="mt-4 overflow-x-auto">
      {/*
        ⚠️ NO `preserveAspectRatio="none"`. Stretching the viewBox to the
        container's width stretches the LABELS with it — every figure rendered
        1.4× wide. The chart keeps its own aspect and is left-aligned instead.
      */}
      <svg
        role="img"
        aria-label={`Waterfall: ${counts.plan} planned, ${stillToRecruit} still to recruit`}
        viewBox={`0 0 ${bars.length * COL} ${H + LABEL_H}`}
        width={bars.length * COL}
        height={H + LABEL_H}
        className="max-w-full"
      >
        {/* The zero line, so the bars are visibly measured from something. */}
        <line x1={0} y1={H} x2={bars.length * COL} y2={H} stroke="#e5e7eb" strokeWidth={1} />

        {bars.map((b, i) => {
          const x = i * COL + GAP / 2;
          const w = COL - GAP;
          const top = H - b.to * scale;
          const height = Math.max(MIN_BAR, (b.to - b.from) * scale);
          const y = b.total ? top : Math.min(top, H - b.from * scale - height);
          return (
            <g key={b.key} data-testid={`waterfall-${b.key}`}>
              {/*
                A connector to the next bar's start height. It is what makes the
                eye read a running total rather than eight unrelated columns.
              */}
              {i < bars.length - 1 && !b.total && (
                <line
                  x1={x + w}
                  y1={H - (b.delta > 0 ? b.to : b.from) * scale}
                  x2={x + w + GAP}
                  y2={H - (b.delta > 0 ? b.to : b.from) * scale}
                  stroke="#d1d5db"
                  strokeWidth={1}
                  strokeDasharray="2 2"
                />
              )}
              <rect x={x} y={y} width={w} height={height} fill={b.fill} rx={1.5} />
              <text
                x={x + w / 2}
                y={y - 4}
                textAnchor="middle"
                fontSize={10}
                fontWeight={600}
                fill="#374151"
              >
                {b.total ? b.delta : `${b.delta > 0 ? '+' : '−'}${Math.abs(b.delta)}`}
              </text>
              <text
                x={x + w / 2}
                y={H + 13}
                textAnchor="middle"
                fontSize={9}
                fill="#6b7280"
              >
                {b.label}
              </text>
            </g>
          );
        })}
      </svg>

      <div className="mt-1 flex flex-wrap gap-x-3.5 gap-y-1 text-[11px] text-gray-500">
        <Key fill={TONE_FILL.delivered} label="delivered" />
        <Key fill={TONE_FILL.paused} label="paused or re-dated" />
        <Key fill={TONE_FILL.dropped} label="cancelled" />
        <Key fill={TONE_FILL.added} label="added to the plan" />
      </div>
    </div>
  );
}

function Key({ fill, label }: { fill: string; label: string }) {
  return (
    <span className="flex items-center gap-1.5">
      <span className="h-2.5 w-2.5 rounded-sm" style={{ backgroundColor: fill }} />
      {label}
    </span>
  );
}
